import moment from 'jalali-moment';

/** Same week anchor as `useWeekManagement` (fa locale week, en `YYYY-MM-DD`). */
export function getReserveWeekStartKey(date?: string): string | null {
  const raw = date?.trim().split('+')[0];
  if (!raw) {
    return null;
  }
  const parsed = moment.from(raw, 'en', 'YYYY-MM-DD');
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.clone().locale('fa').startOf('week').locale('en').format('YYYY-MM-DD');
}

export function reserveWeekStartKeyToMs(weekStartKey: string): number {
  return moment.from(weekStartKey, 'en', 'YYYY-MM-DD').valueOf();
}

/** Rose checkout `weekStartDateTime` for the reserve panel week. */
export function resolveCartWeekStartMs(panelWeekStartEn?: string, mealDate?: string): number {
  const fromPanel = getReserveWeekStartKey(panelWeekStartEn);
  const fromMeal = getReserveWeekStartKey(mealDate);
  const key =
    fromPanel
    ?? fromMeal
    ?? moment().locale('fa').startOf('week').locale('en').format('YYYY-MM-DD');
  return reserveWeekStartKeyToMs(key);
}

export function collectCartLineWeekKeys(
  basketItems: ReadonlyArray<{ food?: { programDate?: string } }>,
): Set<string> {
  const keys = new Set<string>();
  for (const item of basketItems) {
    const lineKey = getReserveWeekStartKey(item.food?.programDate);
    if (lineKey) {
      keys.add(lineKey);
    }
  }
  return keys;
}

/** Week of cart lines; stored ms is ignored when lines exist (avoids stale localStorage). */
export function getCartReserveWeekKey(
  cartWeekStartDateTime: number | null | undefined,
  basketItems: ReadonlyArray<{ food?: { programDate?: string } }>,
): string | null {
  const lineKeys = collectCartLineWeekKeys(basketItems);
  if (lineKeys.size === 1) {
    return [...lineKeys][0];
  }
  if (lineKeys.size > 1) {
    return null;
  }
  if (typeof cartWeekStartDateTime === 'number' && !Number.isNaN(cartWeekStartDateTime)) {
    const fromStored = moment(cartWeekStartDateTime).locale('en').format('YYYY-MM-DD');
    return getReserveWeekStartKey(fromStored);
  }
  return null;
}

export function isCartSingleReserveWeek(
  cartWeekStartDateTime: number | null | undefined,
  basketItems: ReadonlyArray<{ food?: { programDate?: string } }>,
): boolean {
  if (basketItems.length === 0) {
    return true;
  }

  const lineKeys = collectCartLineWeekKeys(basketItems);
  if (lineKeys.size > 1) {
    return false;
  }

  if (lineKeys.size === 1 && typeof cartWeekStartDateTime === 'number' && !Number.isNaN(cartWeekStartDateTime)) {
    const storedKey = getReserveWeekStartKey(
      moment(cartWeekStartDateTime).locale('en').format('YYYY-MM-DD'),
    );
    const lineKey = [...lineKeys][0];
    if (storedKey && storedKey !== lineKey) {
      return false;
    }
  }

  return lineKeys.size <= 1;
}

export function canAddProgramToCartWeek(
  _cartWeekStartDateTime: number | null | undefined,
  basketItems: ReadonlyArray<{ food?: { programDate?: string } }>,
  panelWeekStartEn: string | undefined,
  mealDate: string,
): boolean {
  const lineKeys = collectCartLineWeekKeys(basketItems);
  const newKey =
    getReserveWeekStartKey(panelWeekStartEn)
    ?? getReserveWeekStartKey(mealDate);

  if (lineKeys.size === 0) {
    return true;
  }

  if (lineKeys.size > 1) {
    return false;
  }

  const cartKey = [...lineKeys][0];
  if (!newKey) {
    return false;
  }

  return cartKey === newKey;
}
